import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import Youtube from "@tiptap/extension-youtube";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { useEffect, useRef } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Code, Link2, Image as ImageIcon, YoutubeIcon, Table as TableIcon,
  AlignLeft, AlignCenter, AlignRight, Undo, Redo, Highlighter,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * TipTap-based rich text editor for Research Hub articles.
 * Emits HTML + JSON via onChange({ html, json }).
 */
export default function ArticleEditor({ value, onChange, placeholder }) {
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Highlight,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-blue-700 underline underline-offset-2" } }),
      Image.configure({ inline: false, HTMLAttributes: { class: "rounded-md border border-slate-200 my-4" } }),
      Placeholder.configure({ placeholder: placeholder || "Start writing… drop images, use headings, add tables." }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Youtube.configure({ nocookie: true, HTMLAttributes: { class: "rounded-md my-4 mx-auto" } }),
      Table.configure({ resizable: false }),
      TableRow, TableHeader, TableCell,
    ],
    content: value?.json || value?.html || "",
    onUpdate: ({ editor }) => {
      onChange && onChange({ html: editor.getHTML(), json: editor.getJSON() });
    },
    editorProps: {
      attributes: {
        class: "prose max-w-none focus:outline-none min-h-[400px] px-5 py-4 tiptap",
        "data-testid": "article-editor",
      },
    },
  });

  useEffect(() => {
    if (editor && value?.html && editor.isEmpty && !editor.isFocused) {
      editor.commands.setContent(value.html);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.html]);

  if (!editor) return <div className="text-slate-400 font-mono text-xs">Loading editor…</div>;

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const abs = r.data.url.startsWith("http") ? r.data.url : `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      editor.chain().focus().setImage({ src: abs, alt: r.data.original_name }).run();
      toast.success("Image uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const insertLink = () => {
    const url = window.prompt("URL");
    if (!url) return;
    editor.chain().focus().extendMarkRange("link").setLink({ href: url, target: url.startsWith("http") ? "_blank" : null }).run();
  };
  const insertYoutube = () => {
    const url = window.prompt("YouTube URL");
    if (url) editor.chain().focus().setYoutubeVideo({ src: url }).run();
  };
  const insertTable = () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();

  const btn = (active, onClick, Icon, label, testid) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      data-testid={testid}
      className={`p-1.5 rounded hover:bg-slate-100 ${active ? "bg-slate-200 text-slate-900" : "text-slate-600"}`}
    >
      <Icon className="w-4 h-4" strokeWidth={1.75}/>
    </button>
  );

  return (
    <div className="border border-slate-200 rounded-md bg-white">
      <div className="border-b border-slate-200 px-3 py-2 flex flex-wrap items-center gap-1" data-testid="editor-toolbar">
        {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), Bold, "Bold", "tt-bold")}
        {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), Italic, "Italic", "tt-italic")}
        {btn(editor.isActive("underline"), () => editor.chain().focus().toggleUnderline().run(), UnderlineIcon, "Underline", "tt-underline")}
        {btn(editor.isActive("strike"), () => editor.chain().focus().toggleStrike().run(), Strikethrough, "Strikethrough", "tt-strike")}
        {btn(editor.isActive("highlight"), () => editor.chain().focus().toggleHighlight().run(), Highlighter, "Highlight", "tt-highlight")}
        <span className="w-px h-5 bg-slate-200 mx-1"/>
        {btn(editor.isActive("heading", { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), Heading1, "H1", "tt-h1")}
        {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Heading2, "H2", "tt-h2")}
        {btn(editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), Heading3, "H3", "tt-h3")}
        <span className="w-px h-5 bg-slate-200 mx-1"/>
        {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), List, "Bulleted list", "tt-ul")}
        {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, "Numbered list", "tt-ol")}
        {btn(editor.isActive("blockquote"), () => editor.chain().focus().toggleBlockquote().run(), Quote, "Quote", "tt-quote")}
        {btn(editor.isActive("codeBlock"), () => editor.chain().focus().toggleCodeBlock().run(), Code, "Code block", "tt-code")}
        <span className="w-px h-5 bg-slate-200 mx-1"/>
        {btn({ left: true }.left && editor.isActive({ textAlign: "left" }), () => editor.chain().focus().setTextAlign("left").run(), AlignLeft, "Align left", "tt-align-left")}
        {btn(editor.isActive({ textAlign: "center" }), () => editor.chain().focus().setTextAlign("center").run(), AlignCenter, "Align center", "tt-align-center")}
        {btn(editor.isActive({ textAlign: "right" }), () => editor.chain().focus().setTextAlign("right").run(), AlignRight, "Align right", "tt-align-right")}
        <span className="w-px h-5 bg-slate-200 mx-1"/>
        {btn(editor.isActive("link"), insertLink, Link2, "Link", "tt-link")}
        {btn(false, () => fileRef.current?.click(), ImageIcon, "Insert image", "tt-image")}
        {btn(false, insertYoutube, YoutubeIcon, "Embed YouTube", "tt-youtube")}
        {btn(false, insertTable, TableIcon, "Insert table", "tt-table")}
        <span className="w-px h-5 bg-slate-200 mx-1"/>
        {btn(false, () => editor.chain().focus().undo().run(), Undo, "Undo", "tt-undo")}
        {btn(false, () => editor.chain().focus().redo().run(), Redo, "Redo", "tt-redo")}
        <input ref={fileRef} type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(f); e.target.value = ""; }} className="hidden" data-testid="tt-image-input"/>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
